import { useEffect, useMemo, useState } from 'react';

import { Application } from '@widgets/cabinet/applicationCard/types';

import {
  ApplicationModalPurchaseType,
  newPaymentPurchaseType,
  reissuedCertificatesPurchaseType,
  unusedBoughtCertificatesPurchaseType,
} from '@values/application-purchase';

import { HowToHelpType } from '@shared/enums';
import { countBy, getMiddleElement } from '@shared/utils/arrays';

import { useCreatePurchase } from './useCreatePurchase';
import { useUnusedCertificates } from './useUnusedCertificates';

import { CreatePurchaseRequest, UnusedCertificate } from '@store/api/types';

import { useLanguage } from '@hooks/useLanguage';
import { useApplicationShop } from './useApplicationShop';

interface UseApplicationModalOptions {
  onSuccess?: () => void;
}

const toAllowedAmount = (value: number, count: number) => ({ value, count });

const getAllowedCertificatesAmounts = (certificates: UnusedCertificate[]) =>
  countBy(certificates, (item) => item.amount);

// TODO: refactor

export function useApplicationModal(
  application: Application,
  category: HowToHelpType,
  { onSuccess }: UseApplicationModalOptions = {},
) {
  const language = useLanguage();

  const shop = useApplicationShop(application, category);

  const options = useMemo(() => ({ shopName: shop?.name || 'default' }), [shop?.name]);
  const unusedCertificates = useUnusedCertificates(options);

  const [amounts, setAmounts] = useState(
    () => shop?.config.allowedAmounts.map((amount) => toAllowedAmount(amount, 1)) || [],
  );
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

  const [types, setTypes] = useState([newPaymentPurchaseType]);
  const [type, setType] = useState<string | null>(ApplicationModalPurchaseType.NewPayment);

  const [isOfferAgreementApproved, setIsOfferAgreementApproved] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!shop) {
      return;
    }

    const { allowedAmounts } = shop.config;
    const { bought, returned } = unusedCertificates;

    let amounts = allowedAmounts.map((amount) => toAllowedAmount(amount, 1));
    if (type === ApplicationModalPurchaseType.UnusedBoughtCertificates) {
      amounts = getAllowedCertificatesAmounts(bought);
    }

    if (type === ApplicationModalPurchaseType.ReissueCertificate) {
      amounts = getAllowedCertificatesAmounts(returned);
    }

    setAmounts(amounts);
    setSelectedAmount(getMiddleElement(amounts)?.value || null);
  }, [shop, type]);

  useEffect(() => {
    if (!shop) {
      return;
    }

    const { bought, returned } = unusedCertificates;

    const types = [newPaymentPurchaseType];
    let defaultType = ApplicationModalPurchaseType.NewPayment;

    if (returned.length > 0) {
      types.push(reissuedCertificatesPurchaseType);
      defaultType = ApplicationModalPurchaseType.ReissueCertificate;
    }

    if (bought.length > 0) {
      types.push(unusedBoughtCertificatesPurchaseType);
      defaultType = ApplicationModalPurchaseType.UnusedBoughtCertificates;
    }

    setTypes(types);
    setType(defaultType);
  }, [shop, unusedCertificates]);

  const [createPurchase, { isLoading }] = useCreatePurchase({ onSuccess });

  const onApproveHandler = async () => {
    if (!shop) {
      return;
    }

    setErrors([]);

    if (!selectedAmount) {
      return setErrors(['errors.select-amount']);
    } else if (!isOfferAgreementApproved) {
      return setErrors(['errors.checked']);
    }

    const body: CreatePurchaseRequest = {
      applicationId: application.id,
      type: shop.category,
      locale: language === 'uk' ? 'uk' : 'en',
    };

    const { bought, returned } = unusedCertificates;

    if (type === ApplicationModalPurchaseType.NewPayment) {
      body.amount = selectedAmount;
    } else if (type === ApplicationModalPurchaseType.UnusedBoughtCertificates) {
      const selectedCertificate = bought.find((cert) => cert.amount === selectedAmount);

      body.certificateId = selectedCertificate?.id;
    } else if (type === ApplicationModalPurchaseType.ReissueCertificate) {
      const selectedCertificate = returned.find((cert) => cert.amount === selectedAmount);

      body.certificateId = selectedCertificate?.id;
    }

    await createPurchase(body);
  };

  return {
    amounts,
    type,
    types,

    selectedAmount,

    onApproveHandler,

    isLoading,
    isPaymentDisabled: isLoading || !shop,

    shop,
    errors,

    actions: {
      setType,
      setSelectedAmount,
      setIsOfferAgreementApproved,

      onApproveHandler,
    },
  } as const;
}
